import { useNavigate, useParams } from "react-router-dom";
import {
  Tabs,
  Card,
  Button,
  useToast,
  useModal,
  PageLayout,
  PageHeader,
  Input,
  Toolbar,
  Table,
  THead,
  TBody,
  TRow,
  TH,
  TD,
  Pagination,
  Tooltip,
  Select,
} from "@dawa/ui";
import {
  List,
  Tags,
  FileText,
  Gauge,
  History as HistoryIcon,
  Link as LinkIcon,
  Share2,
  Info,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch, apiUrl } from "../lib/api";
import { OutlineCard } from "../components/OutlineCard";
import { MetaCard } from "../components/MetaCard";
import { ScoreCard } from "../components/ScoreCard";
import { SeoAssistantCard } from "../components/SeoAssistantCard";
import { CopyButton } from "../components/CopyButton";
import { StatusBadge, Skeleton } from "@dawa/ui";

export default function ContentEditor() {
  const { id } = useParams();
  const [outline, setOutline] = useState<string>("");
  const [article, setArticle] = useState<string>("");
  const [meta, setMeta] = useState<any>(null);
  const [score, setScore] = useState<any>(null);
  const [links, setLinks] = useState<any>(null);
  const [primaryKeyword, setPrimaryKeyword] = useState("");
  const [secondaryKeywords, setSecondaryKeywords] = useState<string[]>([]);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [pendingOutline, setPendingOutline] = useState(false);
  const [pendingMeta, setPendingMeta] = useState(false);
  const [pendingScore, setPendingScore] = useState(false);
  const { addToast, Toasts } = useToast();
  const [runningAll, setRunningAll] = useState(false);
  const [creditIssue, setCreditIssue] = useState(false);
  const [runningSteps, setRunningSteps] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdQuery, setCmdQuery] = useState("");
  const { openModal, closeModal, Modal } = useModal();
  const [pendingPatch, setPendingPatch] = useState<{
    before: string;
    after: string;
    note?: string;
  } | null>(null);
  const [sugQ, setSugQ] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectChoices, setProjectChoices] = useState<Array<{ id: string; name: string }>>([]);
  const [voice, setVoice] = useState(false);
  const [voiceTokens, setVoiceTokens] = useState<number | null>(null);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [revQ, setRevQ] = useState("");
  const [revPage, setRevPage] = useState(1);
  const revPageSize = 10;
  const [compareRev, setCompareRev] = useState<any | null>(null);
  const [compareDiff, setCompareDiff] = useState<Array<{ t: "keep" | "add" | "del"; s: string }>>(
    [],
  );
  const [draftQ, setDraftQ] = useState("");
  const [showChecklist, setShowChecklist] = useState(true);
  const [serp, setSerp] = useState<any | null>(null);
  const [serpLoading, setSerpLoading] = useState(false);
  const nav = useNavigate();
  const contentInit = useRef(false);
  const prevState = useRef({ outline: false, meta: false, score: false, draft: false });
  const [newTabs, setNewTabs] = useState<Set<string>>(new Set());
  const [pulseTabs, setPulseTabs] = useState<Set<string>>(new Set());

  const fetchContent = async (token: string) => {
    const res = await apiFetch(`/v1/contents/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const c = await res.json();
    setOutline(c.outline_md ?? "");
    setArticle(c.article_md ?? "");
    setMeta(c.meta_json ?? null);
    setScore(c.score_json ?? null);
    setLinks(c.internal_links_json ?? null);
    setProjectId(c.project_id ?? null);
    setPrimaryKeyword((c.primary_keyword || "").toString());
    const rawSecondary = c.secondary_keywords;
    const parsedSecondary = Array.isArray(rawSecondary)
      ? rawSecondary
      : typeof rawSecondary === "string"
        ? rawSecondary
            .split(",")
            .map((x: string) => x.trim())
            .filter(Boolean)
        : [];
    setSecondaryKeywords(parsedSecondary);
    // Mark new transitions after initial load
    const nowState = {
      outline: !!c.outline_md,
      meta: !!c.meta_json,
      score: !!c.score_json,
      draft: !!c.article_md,
    };
    if (contentInit.current) {
      const next = new Set(newTabs);
      const pulse = new Set(pulseTabs);
      (Object.keys(nowState) as Array<keyof typeof nowState>).forEach((k) => {
        if (!prevState.current[k] && nowState[k]) {
          const id = k === "draft" ? "draft" : (k as string);
          next.add(id);
          pulse.add(id);
          // remove pulse after short delay
          setTimeout(
            () =>
              setPulseTabs((prev) => {
                const s = new Set(prev);
                s.delete(id);
                return s;
              }),
            800,
          );
        }
      });
      setNewTabs(next);
      setPulseTabs(pulse);
    }
    prevState.current = nowState;
    if (!contentInit.current) contentInit.current = true;
    return c;
  };

  // Pipeline helpers
  const stepEta: Record<string, number> = { outline: 6, meta: 2, score: 3, draft: 20 };
  const getPendingSteps = () => {
    const pending: string[] = [];
    if (!outline) pending.push("outline");
    if (!meta) pending.push("meta");
    if (!score) pending.push("score");
    if (!article) pending.push("draft");
    return pending;
  };
  const estimateForPending = () => {
    const steps = getPendingSteps();
    const credits = steps.length;
    const etaSec = steps.reduce((s, id) => s + (stepEta[id] || 3), 0);
    return { steps, credits, etaSec };
  };

  const loadRevisions = async (token: string) => {
    try {
      const r = await apiFetch(`/v1/revisions?contentId=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return setRevisions([]);
      const j = await r.json().catch(() => []);
      setRevisions(Array.isArray(j) ? j : []);
    } catch {
      setRevisions([]);
    }
  };
  async function restoreRevision(rev: any) {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    try {
      const res = await apiFetch(`/v1/contents/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ article_md: rev.article_md }),
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      addToast("Version restaurée", "success");
      await loadRevisions(token!);
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), "error");
    }
  }

  function escapeHtml(s: string) {
    return s.replace(/[&<>]/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));
  }
  function renderArticleWithHighlight(md: string, q: string) {
    if (!q.trim()) return { __html: escapeHtml(md) };
    const safe = escapeHtml(md);
    const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const html = safe.replace(
      re,
      (m) => `<mark class="bg-warning/30 text-text rounded px-0.5">${m}</mark>`,
    );
    return { __html: html };
  }
  function diffLines(a: string, b: string) {
    const A = a.split(/\n/);
    const B = b.split(/\n/);
    const n = A.length,
      m = B.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const out: Array<{ t: "keep" | "add" | "del"; s: string }> = [];
    let i = 0,
      j = 0;
    while (i < n && j < m) {
      if (A[i] === B[j]) {
        out.push({ t: "keep", s: A[i] });
        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        out.push({ t: "del", s: A[i] });
        i++;
      } else {
        out.push({ t: "add", s: B[j] });
        j++;
      }
    }
    while (i < n) {
      out.push({ t: "del", s: A[i++] });
    }
    while (j < m) {
      out.push({ t: "add", s: B[j++] });
    }
    return out;
  }
  function openCompare(rev: any) {
    setCompareRev(rev);
    setCompareDiff(diffLines(rev.article_md || "", article || ""));
    openModal();
  }

  const outlineStatus = outline ? "ready" : runningSteps.includes("outline") ? "pending" : "empty";
  const metaStatus = meta ? "ready" : runningSteps.includes("meta") ? "pending" : "empty";
  const scoreStatus = score ? "ready" : runningSteps.includes("score") ? "pending" : "empty";
  const draftStatus = loadingDraft ? "pending" : article ? "ready" : "empty";

  // ---------- Review Copilot (Phase 2 minimal) ----------
  function computeSuggestions() {
    const list: any[] = [];
    // Rule: Meta length
    const t = meta?.title as string | undefined;
    const d = meta?.description as string | undefined;
    if (!t) {
      // propose from first H1
      const m = /^#\s+(.+)$/m.exec(article || "")?.[1] || outline?.split("\n")[0] || "Titre";
      list.push({
        id: "meta-title-missing",
        type: "meta",
        label: "Ajouter un Title",
        message: "Aucun title détecté.",
        fix: () => ({ meta: { ...(meta || {}), title: m.slice(0, 60) } }),
      });
    } else if (t.length > 60) {
      list.push({
        id: "meta-title-long",
        type: "meta",
        label: "Raccourcir le Title",
        message: `Title (${t.length}) > 60`,
        fix: () => ({ meta: { ...(meta || {}), title: t.slice(0, 60) } }),
      });
    }
    if (!d) {
      const raw = (article || "")
        .replace(/[#>*`_-]/g, " ")
        .slice(0, 200)
        .trim();
      const suggest = raw.length >= 20 ? raw : "Meta description à compléter";
      list.push({
        id: "meta-desc-missing",
        type: "meta",
        label: "Ajouter meta description",
        message: "Aucune meta description.",
        fix: () => ({ meta: { ...(meta || {}), description: suggest.slice(0, 158) } }),
      });
    } else if (d.length < 140 || d.length > 165) {
      const target = d.length > 165 ? d.slice(0, 158) : d.padEnd(150, ".");
      list.push({
        id: "meta-desc-length",
        type: "meta",
        label: "Ajuster meta description",
        message: `Description (${d.length}) hors plage 150–160`,
        fix: () => ({ meta: { ...(meta || {}), description: target } }),
      });
    }
    // Rule: H1 unique
    const h1s = (article || "").split(/\n/).filter((l) => /^#\s+/.test(l));
    if (h1s.length === 0) {
      const src =
        (meta?.title as string) || /^#\s+(.+)$/m.exec(outline || "")?.[1] || "Titre principal";
      list.push({
        id: "h1-missing",
        type: "headings",
        label: "Ajouter H1",
        message: "Aucun H1 détecté.",
        fix: () => ({ article: `# ${src}\n\n${article || ""}` }),
      });
    } else if (h1s.length > 1) {
      list.push({
        id: "h1-multiple",
        type: "headings",
        label: "Démoter H1 en excès",
        message: `Plusieurs H1 (${h1s.length}) détectés.`,
        fix: () => {
          const lines = (article || "").split(/\n/);
          let seen = 0;
          const next = lines.map((line) => {
            if (/^#\s+/.test(line)) {
              seen++;
              return seen === 1 ? line : line.replace(/^#\s+/, "## ");
            }
            return line;
          });
          return { article: next.join("\n") };
        },
      });
    }
    // Rule: Lisibilité (paragraph too long)
    const paras = (article || "").split(/\n\n+/);
    const idx = paras.findIndex((p) => p.replace(/[#>*`_-]/g, "").trim().length > 400);
    if (idx >= 0) {
      list.push({
        id: "readability-long-paragraph",
        type: "readability",
        label: "Scinder un paragraphe long",
        message: "Un paragraphe dépasse 400 caractères.",
        fix: () => {
          const p = paras[idx];
          const split = p.replace(/([.!?])\s+/g, "$1\n\n");
          const newParas = [...paras];
          newParas[idx] = split;
          return { article: newParas.join("\n\n") };
        },
      });
    }
    setSuggestions(list);
  }

  async function applySuggestion(sug: any) {
    const payload = sug.fix();
    // If article change, preview diff
    if (payload.article) {
      setPendingPatch({ before: article || "", after: payload.article, note: sug.label });
      openModal();
      return;
    }
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token || !id) return;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    } as const;
    try {
      if (payload.meta) {
        const newMeta = { ...(meta || {}), ...payload.meta };
        setMeta(newMeta);
        await apiFetch(`/v1/contents/${id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({ meta_json: newMeta }),
        });
      }
      if (payload.article) {
        setArticle(payload.article);
        await apiFetch(`/v1/contents/${id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({ article_md: payload.article }),
        });
        await apiFetch(`/v1/revisions`, {
          method: "POST",
          headers,
          body: JSON.stringify({ contentId: id, article_md: payload.article, note: sug.label }),
        });
      }
      addToast("Suggestion appliquée", "success");
      computeSuggestions();
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), "error");
    }
  }

  async function confirmApplyPatch() {
    if (!pendingPatch) return;
    const { after, note } = pendingPatch;
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token || !id) return;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    } as const;
    try {
      setArticle(after);
      await apiFetch(`/v1/contents/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ article_md: after }),
      });
      await apiFetch(`/v1/revisions`, {
        method: "POST",
        headers,
        body: JSON.stringify({ contentId: id, article_md: after, note }),
      });
      addToast("Patch appliqué", "success");
      computeSuggestions();
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setPendingPatch(null);
      closeModal();
    }
  }

  const pollContent = async (token: string) => {
    for (let i = 0; i < 8; i++) {
      const c = await fetchContent(token);
      if (c?.project_id) {
        try {
          const vp = await apiFetch(`/v1/projects/${c.project_id}/voice-profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const vj = await vp.json().catch(() => ({}));
          const t = typeof vj.tokens === "number" ? vj.tokens : null;
          setVoiceTokens(t);
          setVoice(!!t);
        } catch {}
      }
      await loadRevisions(token);
      if (c.outline_md || c.meta_json || c.score_json || c.article_md) break;
      await new Promise((r) => setTimeout(r, 1000));
    }
  };

  useEffect(() => {
    if (!id || id === "undefined" || !/^[0-9a-fA-F-]{36}$/.test(id)) return;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const s = await supabase.auth.getSession();
      const token = s.data.session?.access_token;
      if (!token) {
        addToast("Non authentifié", "error");
        return;
      }
      const c = await fetchContent(token);
      try {
        const pr = await apiFetch(`/v1/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const pj = await pr.json().catch(() => []);
        if (Array.isArray(pj)) setProjectChoices(pj.map((p: any) => ({ id: p.id, name: p.name })));
      } catch {
        /* ignore */
      }

      // 2) auto-déclenchement si vide (avec feedback erreurs)
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
      async function trigger(
        path: "/v1/generate/outline" | "/v1/generate/meta" | "/v1/generate/score",
      ) {
        try {
          const step = path.split("/").pop()!;
          setRunningSteps((s) => Array.from(new Set([...s, step])));
          const res = await apiFetch(`${path}${voice ? "?voice=1" : "?voice=0"}`, {
            method: "POST",
            headers,
            body: JSON.stringify({ contentId: id }),
          });
          if (res.status === 200) return;
          if (res.status === 409) {
            addToast("Génération déjà en cours", "info");
            return;
          }
          if (res.status === 402) {
            addToast("Crédits insuffisants", "error");
            setCreditIssue(true);
            return;
          }
          const d = await res.json().catch(() => ({}) as any);
          addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
        } catch (e) {
          addToast(e instanceof Error ? e.message : String(e), "error");
        } finally {
          const step = path.split("/").pop()!;
          setRunningSteps((s) => s.filter((x) => x !== step));
        }
      }
      const tasks: Promise<void>[] = [];
      if (!c.outline_md) tasks.push(trigger("/v1/generate/outline"));
      if (!c.meta_json) tasks.push(trigger("/v1/generate/meta"));
      if (!c.score_json) tasks.push(trigger("/v1/generate/score"));
      await Promise.all(tasks);

      if (!c.outline_md || !c.meta_json || !c.score_json || !c.article_md) {
        void pollContent(token);
      }

      computeSuggestions();

      // Load SERP insights (best effort)
      try {
        setSerpLoading(true);
        const body = {
          keyword: c.primary_keyword,
          language: c.language || "fr",
          country: c.country || "FR",
          device: "desktop",
        };
        const res = await apiFetch(`/v1/serp/fetch`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const j = await res.json().catch(() => ({}));
        if (res.ok) setSerp(j.analysis || j);
        else setSerp(null);
      } catch {
        setSerp(null);
      } finally {
        setSerpLoading(false);
      }

      // 3) abonnement temps réel aux UPDATE de la ligne contents
      const channel = supabase
        .channel(`contents:${id}`)
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "contents", filter: `id=eq.${id}` },
          (payload) => {
            const n: any = payload.new;
            if (typeof n.outline_md === "string") setOutline(n.outline_md);
            if (typeof n.article_md === "string") setArticle(n.article_md);
            if (n.meta_json !== undefined) setMeta(n.meta_json);
            if (n.score_json !== undefined) setScore(n.score_json);
            if (n.internal_links_json !== undefined) setLinks(n.internal_links_json);
            computeSuggestions();
            const now = {
              outline: !!n.outline_md,
              meta: !!n.meta_json,
              score: !!n.score_json,
              draft: !!n.article_md,
            };
            const next = new Set(newTabs);
            (Object.keys(now) as Array<keyof typeof now>).forEach((k) => {
              if (!prevState.current[k] && now[k]) {
                const id = k === "draft" ? "draft" : (k as string);
                next.add(id);
                setPulseTabs((prev) => {
                  const s = new Set(prev);
                  s.add(id);
                  return s;
                });
                setTimeout(
                  () =>
                    setPulseTabs((prev) => {
                      const s = new Set(prev);
                      s.delete(id);
                      return s;
                    }),
                  800,
                );
              }
            });
            setNewTabs(next);
            prevState.current = now;
          },
        )
        .subscribe();

      unsubscribe = () => supabase.removeChannel(channel);
    })();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [id, addToast]);

  // Command Palette (Phase 3)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmd = e.metaKey || e.ctrlKey;
      if (isCmd && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const commands = [
    { id: "gen-all", label: "Générer tout", action: () => void generateAll(false) },
    { id: "gen-all-force", label: "Forcer tout", action: () => void generateAll(true) },
    {
      id: "regen-outline",
      label: "Régénérer Plan",
      action: async () => {
        const s = await supabase.auth.getSession();
        const token = s.data.session?.access_token;
        if (!token || !id) return;
        await apiFetch(`/v1/generate/outline${voice ? "?voice=1" : "?voice=0"}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentId: id }),
        });
        addToast("Plan relancé", "info");
      },
    },
    {
      id: "regen-meta",
      label: "Régénérer Meta",
      action: async () => {
        const s = await supabase.auth.getSession();
        const token = s.data.session?.access_token;
        if (!token || !id) return;
        await apiFetch(`/v1/generate/meta${voice ? "?voice=1" : "?voice=0"}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentId: id }),
        });
        addToast("Meta relancé", "info");
      },
    },
    {
      id: "regen-score",
      label: "Recalculer Score",
      action: async () => {
        const s = await supabase.auth.getSession();
        const token = s.data.session?.access_token;
        if (!token || !id) return;
        await apiFetch(`/v1/generate/score${voice ? "?voice=1" : "?voice=0"}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentId: id }),
        });
        addToast("Score relancé", "info");
      },
    },
    { id: "open-copilot", label: "Ouvrir Copilot", action: () => setCmdOpen(false) },
    { id: "open-history", label: "Voir Historique", action: () => nav("/history") },
  ];

  // streaming du brouillon
  const runDraft = async () => {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token || !id || id === "undefined" || !/^[0-9a-fA-F-]{36}$/.test(id)) return;

    setLoadingDraft(true);
    setArticle("");

    const resp = await fetch(apiUrl("/v1/generate/draft"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ contentId: id }),
    });
    if (!resp.ok || !resp.body) {
      const t = await resp.text().catch(() => "");
      const msg =
        resp.status === 402
          ? "Crédits insuffisants"
          : resp.status === 409
            ? "Génération déjà en cours"
            : `Erreur ${resp.status}`;
      addToast(`${msg}${t ? ": " + t : ""}`, "error");
      if (resp.status === 402) setCreditIssue(true);
      setLoadingDraft(false);
      return;
    }

    const reader = resp.body?.getReader();
    const decoder = new TextDecoder();
    let text = "";
    while (true) {
      const { value, done } = await reader!.read();
      if (done) break;
      text += decoder.decode(value);
      setArticle(text);
    }
    setLoadingDraft(false);
  };

  const generateAll = async (force = false) => {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token || !id || id === "undefined" || !/^[0-9a-fA-F-]{36}$/.test(id)) return;
    setRunningAll(true);
    try {
      const { steps } = estimateForPending();
      const res = await apiFetch(
        `/v1/generate/batch${force ? "?force=1" : ""}${(force ? "&" : "?") + (voice ? "voice=1" : "voice=0")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ contentId: id, steps }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (res.status === 200) {
        addToast("Génération lancée", "info");
        await pollContent(token);
      } else if (res.status === 402) {
        addToast("Crédits insuffisants", "error");
        setCreditIssue(true);
      } else if (res.status === 409) {
        addToast("Génération déjà en cours", "info");
      } else {
        addToast(data?.error ? String(data.error) : `Erreur ${res.status}`, "error");
      }
    } catch (e) {
      addToast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setRunningAll(false);
    }
  };

  const baseTabs = [
    {
      id: "links",
      label: (
        <span className="inline-flex items-center gap-1">
          <LinkIcon className="h-4 w-4" /> Liens
        </span>
      ),
      content: (
        <Card className="space-y-3 p-4">
          <pre className="text-sm">{links ? JSON.stringify(links, null, 2) : "—"}</pre>
          <Button
            onClick={async () => {
              const s = await supabase.auth.getSession();
              const token = s.data.session?.access_token;
              if (!token || !id) return;
              const res = await apiFetch("/v1/generate/links", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ contentId: id }),
              });
              if (res.status === 200) {
                await pollContent(token);
              } else if (res.status === 409) {
                addToast("Génération déjà en cours", "info");
              } else if (res.status === 402) {
                addToast("Crédits insuffisants", "error");
                setCreditIssue(true);
              } else {
                const d = await res.json().catch(() => ({}) as any);
                addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
              }
            }}
          >
            Proposer des liens internes
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token || !projectId) return;
                const pagesRes = await apiFetch(`/v1/projects/${projectId}/pages`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const pages = await pagesRes.json().catch(() => []);
                const arr = Array.isArray(pages) ? pages : [];
                const map: Record<string, string> = {};
                for (const p of arr) if (p.slug && p.url) map[String(p.slug)] = String(p.url);
                const linksArr = Array.isArray((links as any)?.links)
                  ? (links as any).links
                  : Array.isArray(links)
                    ? (links as any)
                    : [];
                const items = (linksArr as any[])
                  .map((x: any) => ({
                    anchor: x.anchor || x.text || x.title || x.slug || x.url,
                    slug: x.slug,
                    url: x.url,
                  }))
                  .filter((x: any) => x.anchor)
                  .slice(0, 15);
                const lines = items.map((it: any) => {
                  const href =
                    it.url || (it.slug && map[it.slug]) || (it.slug ? `/${it.slug}` : "#");
                  return `- [${it.anchor}](${href})`;
                });
                const section = `## Liens internes\n\n${lines.join("\n")}`;
                const before = article || "";
                const re = /\n##\s+Liens internes\n[\s\S]*?(?=\n##\s+|$)/;
                let after = before;
                if (re.test(before)) after = before.replace(re, `\n${section}`);
                else after = `${before}\n\n${section}`;
                setPendingPatch({ before, after, note: "Insertion liens internes" });
                openModal();
              } catch (e) {
                addToast(e instanceof Error ? e.message : String(e), "error");
              }
            }}
          >
            Insérer dans l’article
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token || !projectId) return;
                const pagesRes = await apiFetch(`/v1/projects/${projectId}/pages`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const pages = await pagesRes.json().catch(() => []);
                const arr = Array.isArray(pages) ? pages : [];
                const map: Record<string, string> = {};
                for (const p of arr) if (p.slug && p.url) map[String(p.slug)] = String(p.url);
                const linksArr = Array.isArray((links as any)?.links)
                  ? (links as any).links
                  : Array.isArray(links)
                    ? (links as any)
                    : [];
                const before = article || "";
                let after = before;
                function escReg(s: string) {
                  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                }
                for (const it of linksArr as any[]) {
                  const anchor = it.anchor || it.text || it.title || it.slug || it.url;
                  if (!anchor) continue;
                  const href =
                    it.url || (it.slug && map[it.slug]) || (it.slug ? `/${it.slug}` : "#");
                  const re = new RegExp(`(^|[^]])\\b(${escReg(anchor)})\\b`, "i");
                  // Replace first occurrence not immediately following a closing bracket ']' (naive avoidance of existing links)
                  const m = after.match(re);
                  if (m) {
                    after = after.replace(re, (match, p1, p2) => `${p1}[${p2}](${href})`);
                  }
                }
                if (after !== before) {
                  setPendingPatch({ before, after, note: "Liens internes inline" });
                  openModal();
                } else {
                  addToast("Aucune occurrence à lier trouvée", "info");
                }
              } catch (e) {
                addToast(e instanceof Error ? e.message : String(e), "error");
              }
            }}
          >
            Insérer les liens en ligne
          </Button>
        </Card>
      ),
    },
    {
      id: "publish",
      label: (
        <span className="inline-flex items-center gap-1">
          <Share2 className="h-4 w-4" /> Publication
        </span>
      ),
      content: (
        <Card className="space-y-3 p-4">
          <div>
            <div className="text-sm text-muted">Title</div>
            <div className="font-medium">{meta?.title || "—"}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Meta description</div>
            <div className="text-sm">{meta?.description || "—"}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Slug</div>
            <div className="text-sm">{meta?.slug || "—"}</div>
          </div>
          <div>
            <div className="text-sm text-muted">Aperçu contenu</div>
            <div className="prose whitespace-pre-wrap break-words max-h-[60vh] overflow-auto border p-2 rounded">
              {article || "—"}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                const md = `# ${meta?.title || ""}\n\n${article || ""}`.trim();
                const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = (meta?.slug || "article").replace(/[^a-z0-9-_]/gi, "-") + ".md";
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              Télécharger .md
            </Button>
            <Button
              onClick={async () => {
                const html =
                  `<h1>${meta?.title || ""}</h1>\n` +
                  (meta?.description ? `<!-- meta: ${meta.description} -->\n` : "") +
                  `<pre>${(article || "").replace(/[<&>]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c] as string)}</pre>`;
                await navigator.clipboard.writeText(html);
                addToast("HTML copié", "success");
              }}
            >
              Copier HTML
            </Button>
          </div>
        </Card>
      ),
    },
    {
      id: "copilot",
      label: (
        <span className="inline-flex items-center gap-1">
          <FileText className="h-4 w-4" /> Copilot
        </span>
      ),
      content: (
        <div className="space-y-3">
          <Card className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Suggestions d'amélioration</h3>
              <Button size="sm" variant="secondary" onClick={() => computeSuggestions()}>
                Recalculer
              </Button>
            </div>
            <Toolbar
              searchValue={sugQ}
              onSearchChange={setSugQ}
              placeholder="Filtrer les suggestions"
            />
            {suggestions.length === 0 ? (
              <div className="text-sm text-muted">Aucune suggestion pour le moment.</div>
            ) : (
              <ul className="space-y-2">
                {suggestions
                  .filter((s) =>
                    sugQ
                      ? (s.label + " " + s.message).toLowerCase().includes(sugQ.toLowerCase())
                      : true,
                  )
                  .map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-2 rounded border p-2"
                    >
                      <div>
                        <div className="font-medium">{s.label}</div>
                        <div className="text-xs text-muted">{s.message}</div>
                      </div>
                      <Button size="sm" onClick={() => void applySuggestion(s)}>
                        Appliquer
                      </Button>
                    </li>
                  ))}
              </ul>
            )}
          </Card>
          <Card className="space-y-2 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Checklist de génération</h3>
              <Button size="sm" variant="secondary" onClick={() => setShowChecklist((v) => !v)}>
                {showChecklist ? "Masquer" : "Afficher"}
              </Button>
            </div>
            <div
              className={`transition-all duration-300 ${showChecklist ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
            >
              <ul className="space-y-1 text-sm">
                <li className="flex items-center justify-between">
                  <span>Plan {outline ? "✓" : "—"}</span>
                  <Button size="sm" variant="secondary" onClick={() => setRequestedTab("outline")}>
                    Aller au plan
                  </Button>
                </li>
                <li className="flex items-center justify-between">
                  <span>Meta {meta ? "✓" : "—"}</span>
                  <Button size="sm" variant="secondary" onClick={() => setRequestedTab("meta")}>
                    Aller aux metas
                  </Button>
                </li>
                <li className="flex items-center justify-between">
                  <span>Brouillon {article ? "✓" : "—"}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setRequestedTab("draft")}>
                      Aller au brouillon
                    </Button>
                    {!article && (
                      <Button size="sm" onClick={() => void runDraft()}>
                        Générer
                      </Button>
                    )}
                  </div>
                </li>
                <li className="flex items-center justify-between">
                  <span>Score {score ? "✓" : "—"}</span>
                  <Button size="sm" variant="secondary" onClick={() => setRequestedTab("score")}>
                    Aller au score
                  </Button>
                </li>
              </ul>
            </div>
          </Card>
        </div>
      ),
    },
    {
      id: "serp",
      label: (
        <span className="inline-flex items-center gap-1">
          <Tags className="h-4 w-4" /> SERP
        </span>
      ),
      content: (
        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">SERP Insights</div>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                try {
                  setSerpLoading(true);
                  const s = await supabase.auth.getSession();
                  const token = s.data.session?.access_token;
                  const res = await apiFetch(`/v1/serp/fetch`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                      keyword: (await fetchContent(token!))?.primary_keyword,
                      language: "fr",
                      country: "FR",
                    }),
                  });
                  const j = await res.json().catch(() => ({}));
                  if (res.ok) setSerp(j.analysis || j);
                  else setSerp(null);
                } finally {
                  setSerpLoading(false);
                }
              }}
            >
              Rafraîchir
            </Button>
          </div>
          {serpLoading ? (
            <div className="text-sm text-muted">Chargement…</div>
          ) : !serp ? (
            <div className="text-sm text-muted">Aucune analyse SERP disponible.</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm text-muted">Intention dominante</div>
                <div className="text-base font-medium capitalize">
                  {serp.dominant_intent || "—"}
                </div>
                {serp.created_at && (
                  <div className="text-xs text-muted">
                    Snapshot: {new Date(serp.created_at).toLocaleString()}
                  </div>
                )}
                {serp.intents_breakdown && (
                  <ul className="mt-2 text-sm">
                    {Object.entries(serp.intents_breakdown).map(([k, v]: any) => (
                      <li key={k} className="flex items-center justify-between">
                        <span className="capitalize">{k}</span>
                        <span>{Math.round((v as number) * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-sm text-muted">Types de contenu</div>
                <ul className="mt-1 text-sm">
                  {(serp.content_types || []).slice(0, 6).map((t: any) => (
                    <li key={t.type} className="flex items-center justify-between">
                      <span>{t.type}</span>
                      <span>{Math.round((t.ratio || 0) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
              {Array.isArray(serp.results) && serp.results.length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-sm font-medium">Résultats principaux</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="text-left px-2 py-1">#</th>
                          <th className="text-left px-2 py-1">Titre</th>
                          <th className="text-left px-2 py-1">URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serp.results.slice(0, 10).map((r: any) => (
                          <tr key={r.rank}>
                            <td className="px-2 py-1 w-8">{r.rank}</td>
                            <td className="px-2 py-1">{r.title || "—"}</td>
                            <td className="px-2 py-1 truncate max-w-[360px]">
                              <a
                                className="underline"
                                href={r.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {r.url}
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm text-muted">Sous‑sujets</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {(serp.subtopics || []).slice(0, 12).map((s: string) => (
                    <span key={s} className="rounded bg-muted/20 px-2 py-1">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted">Questions (PAA)</div>
                <ul className="list-disc pl-5 text-sm">
                  {(serp.questions || []).slice(0, 8).map((q: string) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
              </div>
              {serp.guidelines && (
                <div className="md:col-span-2">
                  <div className="text-sm text-muted">Guidelines</div>
                  <div className="text-sm whitespace-pre-wrap">{serp.guidelines}</div>
                </div>
              )}
            </div>
          )}
        </Card>
      ),
    },
    {
      id: "outline",
      label: (
        <span className="inline-flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 ${pulseTabs.has("outline") ? "animate-pulse" : ""}`}
          >
            <List className="h-4 w-4" /> Plan
          </span>
          {newTabs.has("outline") && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent transition-opacity duration-200">
              Nouveau
            </span>
          )}
        </span>
      ),
      content: (
        <div className="space-y-3">
          <OutlineCard
            outline={outline}
            loading={runningSteps.includes("outline") && !outline}
            onGenerate={async () => {
              const s = await supabase.auth.getSession();
              const token = s.data.session?.access_token;
              if (!token || !id) return;
              try {
                setRunningSteps((s) => Array.from(new Set([...s, "outline"])));
                const res = await apiFetch(
                  `/v1/generate/outline${voice ? "?voice=1" : "?voice=0"}`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ contentId: id }),
                  },
                );
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "info");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              } finally {
                setRunningSteps((s) => s.filter((x) => x !== "outline"));
              }
            }}
          />
          <div>
            <Button
              onClick={async () => {
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token || !id) return;
                const res = await apiFetch(
                  `/v1/generate/outline${voice ? "?voice=1" : "?voice=0"}`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ contentId: id }),
                  },
                );
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "info");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              }}
            >
              Régénérer le plan
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: "meta",
      label: (
        <span className="inline-flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 ${pulseTabs.has("meta") ? "animate-pulse" : ""}`}
          >
            <Tags className="h-4 w-4" /> Meta
          </span>
          {newTabs.has("meta") && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent transition-opacity duration-200">
              Nouveau
            </span>
          )}
        </span>
      ),
      content: (
        <div className="space-y-3">
          <MetaCard
            meta={meta}
            loading={runningSteps.includes("meta") && !meta}
            onGenerate={async () => {
              const s = await supabase.auth.getSession();
              const token = s.data.session?.access_token;
              if (!token || !id) return;
              try {
                setRunningSteps((s) => Array.from(new Set([...s, "meta"])));
                const res = await apiFetch(`/v1/generate/meta${voice ? "?voice=1" : "?voice=0"}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ contentId: id }),
                });
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "info");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              } finally {
                setRunningSteps((s) => s.filter((x) => x !== "meta"));
              }
            }}
          />
          <div>
            <Button
              aria-label="Régénérer le plan"
              onClick={async () => {
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token || !id) return;
                const res = await apiFetch(`/v1/generate/meta${voice ? "?voice=1" : "?voice=0"}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ contentId: id }),
                });
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "info");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              }}
            >
              Régénérer les metas
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: "draft",
      label: (
        <span className="inline-flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 ${pulseTabs.has("draft") ? "animate-pulse" : ""}`}
          >
            <FileText className="h-4 w-4" /> Brouillon
          </span>
          {newTabs.has("draft") && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent transition-opacity duration-200">
              Nouveau
            </span>
          )}
        </span>
      ),
      content: (
        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">Brouillon</div>
            <div className="flex items-center gap-3">
              {(() => {
                const text = article || "";
                const words = text.trim().split(/\s+/).filter(Boolean).length;
                const minutes = words > 0 ? Math.max(1, Math.round(words / 200)) : 0;
                const chars = text.length;
                const h1 = (text.match(/^#\s+/gm) || []).length;
                const h2 = (text.match(/^##\s+/gm) || []).length;
                const h3 = (text.match(/^###\s+/gm) || []).length;
                const links = (text.match(/\[[^\]]+]\([^)]+\)/g) || []).length;
                const images = (text.match(/!\[[^\]]*]\([^)]+\)/g) || []).length;
                return (
                  <span className="text-xs text-muted">
                    {words} mots • ~{minutes} min • {chars} chars • H1/H2/H3 {h1}/{h2}/{h3} • Liens{" "}
                    {links} • Images {images}
                  </span>
                );
              })()}
              <CopyButton text={article || ""} label="Copier" />
              {article && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const blob = new Blob([article], { type: "text/markdown;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `draft-${id}.md`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Télécharger
                </Button>
              )}
            </div>
          </div>
          <Toolbar
            searchValue={draftQ}
            onSearchChange={setDraftQ}
            placeholder="Rechercher dans le brouillon"
          />
          {loadingDraft ? (
            <div className="space-y-3">
              <Skeleton lines={1} />
              <Skeleton lines={1} />
              <Skeleton lines={1} />
              <Skeleton lines={1} />
              <Skeleton lines={1} />
              <Skeleton lines={1} />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <div
                className="prose whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={renderArticleWithHighlight(article || "", draftQ)}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button onClick={runDraft} disabled={loadingDraft} aria-label="Générer le brouillon">
              {loadingDraft ? "Génération…" : "Générer le brouillon"}
            </Button>
          </div>
        </Card>
      ),
    },
    {
      id: "score",
      label: (
        <span className="inline-flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 ${pulseTabs.has("score") ? "animate-pulse" : ""}`}
          >
            <Gauge className="h-4 w-4" /> Score
          </span>
          {newTabs.has("score") && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent transition-opacity duration-200">
              Nouveau
            </span>
          )}
        </span>
      ),
      content: (
        <div className="space-y-3">
          <ScoreCard
            score={score}
            loading={runningSteps.includes("score") && !score}
            onGenerate={async () => {
              const s = await supabase.auth.getSession();
              const token = s.data.session?.access_token;
              if (!token || !id) return;
              try {
                setRunningSteps((s) => Array.from(new Set([...s, "score"])));
                const res = await apiFetch(`/v1/generate/score${voice ? "?voice=1" : "?voice=0"}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ contentId: id }),
                });
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "warning");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              } finally {
                setRunningSteps((s) => s.filter((x) => x !== "score"));
              }
            }}
          />
          <SeoAssistantCard
            article={article || ""}
            meta={meta}
            primaryKeyword={primaryKeyword}
            secondaryKeywords={secondaryKeywords}
            serp={serp}
          />
          <div>
            <Button
              aria-label="Recalculer le score"
              onClick={async () => {
                const s = await supabase.auth.getSession();
                const token = s.data.session?.access_token;
                if (!token || !id) return;
                const res = await apiFetch(`/v1/generate/score${voice ? "?voice=1" : "?voice=0"}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ contentId: id }),
                });
                if (res.status === 200) {
                  await pollContent(token);
                } else if (res.status === 409) {
                  addToast("Génération déjà en cours", "warning");
                } else if (res.status === 402) {
                  addToast("Crédits insuffisants", "error");
                  setCreditIssue(true);
                } else {
                  const d = await res.json().catch(() => ({}) as any);
                  addToast(d?.error ? String(d.error) : `Erreur ${res.status}`, "error");
                }
              }}
            >
              Recalculer le score
            </Button>
          </div>
        </div>
      ),
    },
    {
      id: "history",
      label: (
        <span className="inline-flex items-center gap-1">
          <HistoryIcon className="h-4 w-4" /> Historique
        </span>
      ),
      content: (
        <Card className="space-y-3 p-4">
          <Toolbar
            searchValue={revQ}
            onSearchChange={(v) => {
              setRevQ(v);
              setRevPage(1);
            }}
            placeholder="Rechercher une révision (note, date)"
          />
          {(() => {
            const filtered = revQ
              ? revisions.filter((r) => {
                  const note = String(r.note || "");
                  const date = new Date(r.created_at).toLocaleString();
                  return (note + " " + date).toLowerCase().includes(revQ.toLowerCase());
                })
              : revisions;
            if (!filtered.length) return <div className="text-sm text-muted">Aucune révision</div>;
            const total = filtered.length;
            const slice = filtered.slice((revPage - 1) * revPageSize, revPage * revPageSize);
            return (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <THead>
                      <TRow>
                        <TH>Date</TH>
                        <TH>Note</TH>
                        <TH>Actions</TH>
                      </TRow>
                    </THead>
                    <TBody>
                      {slice.map((r) => (
                        <TRow key={r.id}>
                          <TD>{new Date(r.created_at).toLocaleString()}</TD>
                          <TD className="text-muted">{r.note || "—"}</TD>
                          <TD className="whitespace-nowrap">
                            <Button size="sm" variant="secondary" onClick={() => openCompare(r)}>
                              Comparer
                            </Button>
                            <Button
                              size="sm"
                              className="ml-2"
                              onClick={() => void restoreRevision(r)}
                            >
                              Restaurer
                            </Button>
                          </TD>
                        </TRow>
                      ))}
                    </TBody>
                  </Table>
                </div>
                <Pagination
                  page={revPage}
                  pageSize={revPageSize}
                  total={total}
                  onChange={setRevPage}
                />
              </>
            );
          })()}
        </Card>
      ),
    },
  ];
  const order =
    article && article.length > 0
      ? ["draft", "outline", "meta", "links", "score", "publish", "copilot", "history"]
      : ["outline", "meta", "draft", "links", "score", "publish", "copilot", "history"];
  const tabsDict = Object.fromEntries(baseTabs.map((t: any) => [t.id, t] as const));
  const tabs = order.filter((id) => !!tabsDict[id]).map((id) => tabsDict[id]);
  const defaultActiveId = tabs[0]?.id;
  const [requestedTab, setRequestedTab] = useState<string | null>(null);
  const nextIncompleteTabId = (() => {
    const seq = ["draft", "outline", "meta", "score"];
    for (const id of seq) {
      if (id === "draft" && !article) return id;
      if (id === "outline" && !outline) return id;
      if (id === "meta" && !meta) return id;
      if (id === "score" && !score) return id;
    }
    return defaultActiveId || null;
  })();

  // Clear "Nouveau" marker when navigating to a tab explicitly
  useEffect(() => {
    if (!requestedTab) return;
    setNewTabs((prev) => {
      const s = new Set(prev);
      s.delete(requestedTab);
      return s;
    });
  }, [requestedTab]);

  return (
    <PageLayout maxWidth="full" className="space-y-4">
      <PageHeader
        title="Éditeur"
        breadcrumbs={[{ label: "Contenus", href: "/contents" }, { label: String(id || "") }]}
        actions={
          <Button size="sm" onClick={() => nav(`/history?contentId=${id}`)}>
            Historique
          </Button>
        }
      />
      <div className="sticky top-0 z-10 -mx-6 mb-2 border-b bg-surface/80 p-3 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="mr-2 text-xs uppercase text-muted">Statut</span>
            <Tooltip
              label={`Plan: ${outlineStatus === "ready" ? "prêt" : outlineStatus === "pending" ? "en cours" : "vide"}`}
            >
              <span>
                <StatusBadge status={outlineStatus as any} label="Plan" />
              </span>
            </Tooltip>
            <Tooltip
              label={`Meta: ${metaStatus === "ready" ? "prêt" : metaStatus === "pending" ? "en cours" : "vide"}`}
            >
              <span>
                <StatusBadge status={metaStatus as any} label="Meta" />
              </span>
            </Tooltip>
            <Tooltip
              label={`Brouillon: ${draftStatus === "ready" ? "prêt" : draftStatus === "pending" ? "en cours" : "vide"}`}
            >
              <span>
                <StatusBadge status={draftStatus as any} label="Brouillon" />
              </span>
            </Tooltip>
            <Tooltip
              label={`Score: ${scoreStatus === "ready" ? "prêt" : scoreStatus === "pending" ? "en cours" : "vide"}`}
            >
              <span>
                <StatusBadge status={scoreStatus as any} label="Score" />
              </span>
            </Tooltip>
            <span className="ml-2 text-xs text-muted">
              Étapes restantes: {getPendingSteps().length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 text-sm">
              <span className="text-xs text-muted">Projet</span>
              <Select
                value={projectId || ""}
                onChange={async (e) => {
                  const next = e.target.value || null;
                  setProjectId(next);
                  try {
                    const s = await supabase.auth.getSession();
                    const token = s.data.session?.access_token;
                    if (!token || !id) return;
                    const res = await apiFetch(`/v1/contents/${id}`, {
                      method: "PATCH",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ project_id: next }),
                    });
                    addToast(
                      res.ok ? "Projet mis à jour" : "Échec de l'association au projet",
                      res.ok ? "success" : "error",
                    );
                  } catch {}
                }}
              >
                <option value="">Aucun</option>
                {projectChoices.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            {projectId && (
              <div className="mr-2 inline-flex items-center gap-2 text-sm">
                <input
                  id="use-voice"
                  type="checkbox"
                  checked={voice}
                  onChange={(e) => setVoice(e.target.checked)}
                />
                <label htmlFor="use-voice">
                  Utiliser le ton du projet{voiceTokens !== null ? ` (~${voiceTokens} tokens)` : ""}
                </label>
                <Tooltip
                  label="Le profil de ton synthétise le style des articles de référence. Des extraits courts peuvent être inclus si le budget tokens le permet."
                  placement="bottom"
                >
                  <span className="inline-flex cursor-help">
                    <Info className="h-4 w-4" />
                  </span>
                </Tooltip>
              </div>
            )}
            <Button size="sm" onClick={() => generateAll(false)} disabled={runningAll}>
              {runningAll ? "Génération…" : "Générer tout"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => generateAll(true)}
              disabled={runningAll}
            >
              Forcer tout
            </Button>
            {getPendingSteps().length === 0 && (
              <Button size="sm" onClick={() => setRequestedTab("publish")}>
                Terminer
              </Button>
            )}
            {article && <CopyButton text={article} label="Copier brouillon" />}
            <Button
              size="sm"
              variant="secondary"
              aria-label="Exporter JSON complet"
              onClick={() => {
                try {
                  const payload = {
                    id,
                    outline,
                    meta,
                    score,
                    article,
                    exportedAt: new Date().toISOString(),
                  };
                  const json = JSON.stringify(payload, null, 2);
                  const blob = new Blob([json], { type: "application/json;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  const base = (meta?.slug || meta?.title || id || "export")
                    .toString()
                    .toLowerCase()
                    .replace(/[^a-z0-9-_]/g, "-");
                  a.href = url;
                  a.download = `${base}-content.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch {
                  // noop
                }
              }}
            >
              Exporter JSON
            </Button>
          </div>
        </div>
        {(() => {
          const done = (outline ? 1 : 0) + (meta ? 1 : 0) + (article ? 1 : 0) + (score ? 1 : 0);
          const pct = Math.round((done / 4) * 100);
          return (
            <div
              className="mt-2 cursor-pointer"
              onClick={() => setRequestedTab(nextIncompleteTabId)}
              title="Aller à la prochaine étape incomplète"
            >
              <div className="h-1 w-full rounded bg-muted/30">
                <div
                  className="h-1 rounded bg-accent transition-[width] duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1 text-right text-xs text-muted">{pct}%</div>
            </div>
          );
        })()}
      </div>
      <Modal>
        {pendingPatch ? (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Aperçu des modifications</h3>
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <div className="text-sm text-muted">Avant</div>
                <pre className="max-h-64 overflow-auto rounded border p-2 whitespace-pre-wrap">
                  {pendingPatch.before}
                </pre>
              </div>
              <div>
                <div className="text-sm text-muted">Après</div>
                <pre className="max-h-64 overflow-auto rounded border p-2 whitespace-pre-wrap">
                  {pendingPatch.after}
                </pre>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setPendingPatch(null);
                  closeModal();
                }}
              >
                Annuler
              </Button>
              <Button onClick={() => void confirmApplyPatch()}>Appliquer</Button>
            </div>
          </div>
        ) : compareRev ? (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Comparer la révision</h3>
            <div className="max-h-[60vh] overflow-auto rounded border">
              <pre className="whitespace-pre-wrap p-2 text-sm">
                {compareDiff.map((l, idx) => (
                  <div
                    key={idx}
                    className={
                      l.t === "add" ? "text-success" : l.t === "del" ? "text-danger" : "text-text"
                    }
                  >
                    {l.t === "add" ? "+ " : l.t === "del" ? "- " : "  "}
                    {l.s}
                  </div>
                ))}
              </pre>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setCompareRev(null);
                  setCompareDiff([]);
                  closeModal();
                }}
              >
                Fermer
              </Button>
              <Button onClick={() => void restoreRevision(compareRev!)}>
                Restaurer cette version
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
      {/* Command Palette */}
      {cmdOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4"
          onClick={() => setCmdOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-md bg-surface p-2 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              autoFocus
              value={cmdQuery}
              onChange={(e) => setCmdQuery(e.target.value)}
              placeholder="Taper une commande (⌘K pour fermer)"
              className="w-full"
            />
            <ul className="mt-2 max-h-64 overflow-auto">
              {commands
                .filter((c) => c.label.toLowerCase().includes(cmdQuery.toLowerCase()))
                .map((c) => (
                  <li key={c.id}>
                    <button
                      className="w-full text-left px-2 py-1 hover:bg-muted/10 rounded"
                      onClick={() => {
                        setCmdOpen(false);
                        setCmdQuery("");
                        void c.action();
                      }}
                    >
                      {c.label}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
      {/* Pipeline summary */}
      <Card className="mb-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {(["outline", "meta", "score", "draft"] as const).map((sid) => {
              const done =
                sid === "outline"
                  ? !!outline
                  : sid === "meta"
                    ? !!meta
                    : sid === "score"
                      ? !!score
                      : !!article;
              const running = runningAll && !done;
              const label =
                sid === "outline"
                  ? "Plan"
                  : sid === "meta"
                    ? "Meta"
                    : sid === "score"
                      ? "Score"
                      : "Brouillon";
              return (
                <span
                  key={sid}
                  className={`rounded-full px-2 py-1 text-xs ${done ? "bg-success/10 text-success" : running ? "bg-warning/10 text-warning" : "bg-surface text-muted"}`}
                >
                  {label} {done ? "✓" : running ? "…" : ""}
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {(() => {
              const { credits, etaSec } = estimateForPending();
              return (
                <span className="text-sm text-muted">
                  ~{etaSec}s • {credits} crédit{credits > 1 ? "s" : ""}
                </span>
              );
            })()}
            <Button onClick={() => generateAll(false)} disabled={runningAll}>
              {runningAll ? "Génération…" : "Générer tout"}
            </Button>
            <Button onClick={() => generateAll(true)} variant="secondary" disabled={runningAll}>
              Forcer tout
            </Button>
          </div>
        </div>
      </Card>
      {!id || id === "undefined" || !/^[0-9a-fA-F-]{36}$/.test(id) ? (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning/10 p-3 text-warning">
          Identifiant de contenu invalide ou manquant. Retournez à vos contenus et réessayez.
        </div>
      ) : null}
      <div className="mb-3 flex flex-col gap-2">
        {creditIssue && (
          <div className="flex items-center justify-between rounded-md border border-danger/30 bg-danger/10 p-3 text-danger">
            <span>Crédits insuffisants pour lancer la génération.</span>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => nav("/credits")}>
                Acheter des crédits
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setCreditIssue(false)}>
                Fermer
              </Button>
            </div>
          </div>
        )}
        {/* Title moved to PageHeader */}
      </div>
      <Tabs
        key={(requestedTab || defaultActiveId) + ":" + tabs.map((t) => t.id).join(",")}
        tabs={tabs}
        defaultActiveId={requestedTab || defaultActiveId}
      />
      <Toasts />
    </PageLayout>
  );
}
